import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { post, remove } from "../../redux/ApiHelper";
import { successToast } from "../../utils/toast.util";
import { QuestionPaperInterface } from "../../interfaces/question-paper.interface";
import { joinSubjectChips, normalizeSubjectList } from "../../utils/subjectChips.util";

type SignedAdminLike = {
  email: string;
  role: string;
  type: string;
};

type FieldErrors = {
  subject?: string;
  className?: string;
  difficultyLevel?: string;
  count?: string;
  marksPerQuestion?: string;
  negativeMarks?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  questionPaperId: string;
  subjects: string[];
  defaultSubject?: string;
  lockSubject?: boolean;
  multiSubject?: boolean;
  defaultClass?: string;
  lockClass?: boolean;
  fullMarks: number;
  signedAdmin: SignedAdminLike;
  onGenerated: (updatedPaper: QuestionPaperInterface) => void;
};

export default function AiQuestionPaperGeneratorSheet({
  open,
  onClose,
  questionPaperId,
  subjects,
  defaultSubject,
  lockSubject,
  multiSubject,
  defaultClass,
  lockClass,
  fullMarks,
  signedAdmin,
  onGenerated,
}: Props) {
  const classOptions = useMemo(
    () => Array.from({ length: 12 }).map((_, i) => `class ${i + 1}`),
    []
  );

  const [subject, setSubject] = useState(defaultSubject || "");
  const [className, setClassName] = useState(defaultClass || "");
  const [difficultyLevel, setDifficultyLevel] = useState<"easy" | "medium" | "hard">("medium");
  const [count, setCount] = useState<number>(10);
  const [marksPerQuestion, setMarksPerQuestion] = useState<number>(1);
  const [negativeMarks, setNegativeMarks] = useState<number>(0);
  const [extraPrompt, setExtraPrompt] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const subjectChips = useMemo(() => normalizeSubjectList(subject), [subject]);
  const subjectOptions = useMemo(() => {
    const seen = new Set(subjectChips.map((s) => s.toLowerCase()));
    return [
      ...subjectChips,
      ...subjects.filter((s) => {
        const key = String(s).toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    ];
  }, [subjects, subjectChips]);

  useEffect(() => {
    if (!open) return;
    setSubject(
      multiSubject
        ? joinSubjectChips(normalizeSubjectList(defaultSubject))
        : defaultSubject || ""
    );
    setClassName(defaultClass || "");
    setErrors({});
  }, [open]);

  const generatedTotalMarks = useMemo(() => {
    const safeCount = Number.isFinite(count) ? count : 0;
    const safeMarks = Number.isFinite(marksPerQuestion) ? marksPerQuestion : 0;
    return safeCount * safeMarks;
  }, [count, marksPerQuestion]);

  const marksMatch = fullMarks > 0 && generatedTotalMarks === fullMarks;
  const marksDelta = fullMarks - generatedTotalMarks;
  const marksStatus =
    fullMarks <= 0
      ? { tone: "info" as const, label: "Set full marks in the question paper to enable AI generation." }
      : marksMatch
        ? { tone: "success" as const, label: "Marks match. You can generate now." }
        : marksDelta > 0
          ? { tone: "warning" as const, label: `${marksDelta} marks short. Increase count/marks per question.` }
          : { tone: "warning" as const, label: `Over by ${Math.abs(marksDelta)} marks. Decrease count/marks per question.` };

  const clearError = (key: keyof FieldErrors) => {
    if (!errors[key]) return;
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const makeTextFieldSx = (hasError?: boolean) =>
    ({
      "& .MuiOutlinedInput-root": {
        color: "#ffffff",
        "& fieldset": { borderColor: hasError ? "#ff0000" : "rgba(255,255,255,0.45)" },
        "&:hover fieldset": { borderColor: hasError ? "#ff0000" : "rgba(255,255,255,0.75)" },
        "&.Mui-focused fieldset": { borderColor: hasError ? "#ff0000" : "#fcd307" },
      },
      "& .MuiInputLabel-root": { color: "#ffffff" },
      "& .MuiFormHelperText-root": { color: "#ff0000" },
    }) as const;

  const makeSelectSx = (hasError?: boolean) =>
    ({
      color: "#ffffff",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "#ff0000" : "rgba(255,255,255,0.45)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "#ff0000" : "rgba(255,255,255,0.75)",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "#ff0000" : "#fcd307",
      },
      "& .MuiSvgIcon-root": { color: "#ffffff" },
    }) as const;

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    const hasSubject = multiSubject ? subjectChips.length > 0 : !!String(subject || "").trim();
    if (!hasSubject) next.subject = "Subject is required";
    if (!String(className || "").trim()) next.className = "Class is required";
    if (!difficultyLevel) next.difficultyLevel = "Difficulty is required";
    if (!Number.isFinite(count) || count < 1 || count > 200) {
      next.count = "Count must be between 1 and 200";
    }
    if (!Number.isFinite(marksPerQuestion) || marksPerQuestion <= 0) {
      next.marksPerQuestion = "Marks per question must be greater than 0";
    }
    if (!Number.isFinite(negativeMarks) || negativeMarks < 0) {
      next.negativeMarks = "Negative marks must be 0 or greater";
    }
    if (!marksMatch) {
      next.count = next.count || marksStatus.label;
      next.marksPerQuestion = next.marksPerQuestion || marksStatus.label;
    }
    return next;
  }

  async function handleGenerate() {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean) || !questionPaperId) return;

    try {
      setLoading(true);
      await remove(`/admin/examination/question-paper/${questionPaperId}/questions`);
      const resp = await post(
        `/admin/examination/question-paper/${questionPaperId}/generate-ai`,
        {
          subject: multiSubject ? joinSubjectChips(subjectChips) : subject,
          class: className,
          difficultyLevel,
          count,
          marksPerQuestion,
          negativeMarks,
          extraPrompt: extraPrompt?.trim() || "",
          adminEmail: signedAdmin.email,
          adminRole: signedAdmin.role,
          adminType: signedAdmin.type,
        }
      );

      if (resp?.data) {
        successToast(resp?.message || "Questions generated");
        onGenerated(resp.data as QuestionPaperInterface);
        onClose();
      }
    } catch {
      // ApiHelper already toasts
    } finally {
      setLoading(false);
    }
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: "#0f0f0f",
          color: "#ffffff",
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          px: { xs: 2, sm: 3 },
          py: 2,
        },
      }}
    >
      <Box sx={{ maxWidth: 980, mx: "auto", width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 700 }}>
              Generate Questions by AI
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)", mt: 0.5 }}>
              Fill the fields below. We’ll generate MCQ questions in JSON and store them in the question bank.
            </Typography>
          </Box>
          <Button
            onClick={onClose}
            sx={{
              color: "#fcd307",
              borderColor: "rgba(252, 211, 7, 0.45)",
              "&:hover": { borderColor: "rgba(252, 211, 7, 0.75)", backgroundColor: "rgba(252, 211, 7, 0.08)" },
            }}
            variant="outlined"
          >
            Close
          </Button>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", my: 2 }} />

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
          <Chip
            label={`Full marks: ${fullMarks || "—"}`}
            sx={{
              bgcolor: "rgba(255,255,255,0.08)",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.14)",
            }}
            variant="outlined"
          />
          <Chip
            label={`This generation: ${generatedTotalMarks || "—"}`}
            sx={{
              bgcolor: "rgba(255,255,255,0.08)",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.14)",
            }}
            variant="outlined"
          />
        </Box>

        {!marksMatch ? (
          <Alert
            severity={marksStatus.tone}
            sx={{
              mb: 2,
              backgroundColor: "rgba(33, 150, 243, 0.10)",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.14)",
              "& .MuiAlert-icon": { color: "#fcd307" },
            }}
          >
            {marksStatus.label}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "2fr 1fr 1fr" },
            gap: 2,
          }}
        >
          {lockSubject ? (
            <Box>
              <Box
                sx={{
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 1.5,
                  p: 1.25,
                  backgroundColor: "rgba(255,255,255,0.03)",
                }}
              >
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                  Subject
                </Typography>
                <Typography variant="body2" sx={{ color: "#ffffff", fontWeight: 800 }}>
                  {subject || "—"}
                </Typography>
              </Box>
              {errors.subject ? (
                <Typography variant="caption" sx={{ color: "#ff0000", mt: 0.5, ml: 1.75 }}>
                  {errors.subject}
                </Typography>
              ) : null}
            </Box>
          ) : multiSubject ? (
            <Autocomplete
              multiple
              freeSolo
              options={subjectOptions}
              value={subjectChips}
              onChange={(_e, value) => {
                setSubject(joinSubjectChips(normalizeSubjectList(value as string[])));
                clearError("subject");
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={`${option}-${index}`}
                    label={option}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(252, 211, 7, 0.18)",
                      color: "#ffffff",
                      "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" },
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Subjects"
                  required
                  error={!!errors.subject}
                  helperText={errors.subject}
                  sx={makeTextFieldSx(!!errors.subject)}
                />
              )}
              sx={{
                "& .MuiAutocomplete-inputRoot": { color: "#ffffff" },
                "& .MuiSvgIcon-root": { color: "#ffffff" },
              }}
            />
          ) : (
            <FormControl fullWidth error={!!errors.subject} required>
              <InputLabel sx={{ color: "#ffffff" }}>Subject</InputLabel>
              <Select
                value={subject}
                label="Subject"
                onChange={(e) => {
                  setSubject(String(e.target.value));
                  clearError("subject");
                }}
                sx={makeSelectSx(!!errors.subject)}
              >
                {subjects.map((s) => (
                  <MenuItem key={s} value={s} sx={{ color: "#000000" }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
              {errors.subject ? <FormHelperText>{errors.subject}</FormHelperText> : null}
            </FormControl>
          )}

          {lockClass ? (
            <Box>
              <Box
                sx={{
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 1.5,
                  p: 1.25,
                  backgroundColor: "rgba(255,255,255,0.03)",
                }}
              >
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                  Class
                </Typography>
                <Typography variant="body2" sx={{ color: "#ffffff", fontWeight: 800 }}>
                  {className || "—"}
                </Typography>
              </Box>
              {errors.className ? (
                <Typography variant="caption" sx={{ color: "#ff0000", mt: 0.5, ml: 1.75 }}>
                  {errors.className}
                </Typography>
              ) : null}
            </Box>
          ) : (
            <FormControl fullWidth error={!!errors.className} required>
              <InputLabel sx={{ color: "#ffffff" }}>Class</InputLabel>
              <Select
                value={className}
                label="Class"
                onChange={(e) => {
                  setClassName(String(e.target.value));
                  clearError("className");
                }}
                sx={makeSelectSx(!!errors.className)}
              >
                {classOptions.map((c) => (
                  <MenuItem key={c} value={c} sx={{ color: "#000000" }}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
              {errors.className ? <FormHelperText>{errors.className}</FormHelperText> : null}
            </FormControl>
          )}

          <FormControl fullWidth error={!!errors.difficultyLevel} required>
            <InputLabel sx={{ color: "#ffffff" }}>Difficulty</InputLabel>
            <Select
              value={difficultyLevel}
              label="Difficulty"
              onChange={(e) => {
                setDifficultyLevel(e.target.value as any);
                clearError("difficultyLevel");
              }}
              sx={makeSelectSx(!!errors.difficultyLevel)}
            >
              <MenuItem value="easy" sx={{ color: "#000000" }}>
                easy
              </MenuItem>
              <MenuItem value="medium" sx={{ color: "#000000" }}>
                medium
              </MenuItem>
              <MenuItem value="hard" sx={{ color: "#000000" }}>
                hard
              </MenuItem>
            </Select>
            {errors.difficultyLevel ? <FormHelperText>{errors.difficultyLevel}</FormHelperText> : null}
          </FormControl>

          <TextField
            type="number"
            label="Number of questions"
            value={count}
            onChange={(e) => {
              setCount(Number(e.target.value));
              clearError("count");
            }}
            fullWidth
            error={!!errors.count}
            helperText={errors.count}
            sx={makeTextFieldSx(!!errors.count)}
          />

          <TextField
            type="number"
            label="Marks per question"
            value={marksPerQuestion}
            onChange={(e) => {
              setMarksPerQuestion(Number(e.target.value));
              clearError("marksPerQuestion");
            }}
            fullWidth
            error={!!errors.marksPerQuestion}
            helperText={errors.marksPerQuestion}
            sx={makeTextFieldSx(!!errors.marksPerQuestion)}
          />

          <TextField
            type="number"
            label="Negative marks (optional)"
            value={negativeMarks}
            onChange={(e) => {
              setNegativeMarks(Number(e.target.value));
              clearError("negativeMarks");
            }}
            fullWidth
            error={!!errors.negativeMarks}
            helperText={errors.negativeMarks}
            sx={makeTextFieldSx(!!errors.negativeMarks)}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <TextField
            label="Extra prompt (optional)"
            value={extraPrompt}
            onChange={(e) => setExtraPrompt(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Example: Focus more on algebra word problems, avoid trick questions, keep language simple…"
            sx={{
              ...makeTextFieldSx(false),
              "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.45)" },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", mt: 2 }}>
          <Button
            onClick={handleGenerate}
            disabled={!questionPaperId || loading}
            variant="contained"
            sx={{
              backgroundColor: "#fcd307",
              color: "#000000",
              fontWeight: 700,
              px: 3,
              "&:hover": { backgroundColor: "#e6be06" },
              "&.Mui-disabled": { backgroundColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" },
            }}
          >
            {loading ? "Generating..." : "Generate & Attach"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
